/**
 * Browser canvas helpers for the pre-processing pipeline: decode a data
 * URL/Blob/image into {@link ImagePixels}, and encode {@link ImagePixels} back
 * to a canvas or data URL. These are the only DOM-touching pieces; the core
 * stages never import them, so a Lambda can pass raw pixels directly.
 */
import { ImagePixels } from '../../core/ports';

/** Decode a data URL (or any image src) to RGBA pixels. */
export async function decodeImage(src: string): Promise<ImagePixels> {
  const img = new Image();
  img.src = src;
  await img.decode();
  return imageElementToPixels(img);
}

/** Read an <img>/canvas/video frame into RGBA pixels at its natural size. */
export function imageElementToPixels(
  el: HTMLImageElement | HTMLCanvasElement,
): ImagePixels {
  const w = el instanceof HTMLImageElement ? el.naturalWidth : el.width;
  const h = el instanceof HTMLImageElement ? el.naturalHeight : el.height;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(el, 0, 0);
  const id = ctx.getImageData(0, 0, w, h);
  return { data: id.data, width: w, height: h };
}

/** Render {@link ImagePixels} into a fresh canvas. */
export function pixelsToCanvas(img: ImagePixels): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  const ctx = c.getContext('2d')!;
  const id = new ImageData(new Uint8ClampedArray(img.data), img.width, img.height);
  ctx.putImageData(id, 0, 0);
  return c;
}

/** Encode {@link ImagePixels} to a JPEG/PNG data URL. */
export function pixelsToDataUrl(img: ImagePixels, type = 'image/jpeg', quality = 0.92): string {
  return pixelsToCanvas(img).toDataURL(type, quality);
}
